import type { User } from "@/models/user"
import { httpClient, isConnectivityError, toHttpError } from "@/utils/api"

interface LoginResponse {
  authenticationToken: string
  success: boolean
}

export type SignInResult =
  | "connectivity"
  | "invalid_credentials"
  | "server_error"
  | "success"

export type SignInFailure = Exclude<SignInResult, "success">

const INVALID_CREDENTIAL_STATUSES = new Set([400, 401, 422])

export const classifySignInFailure = (error: unknown): SignInFailure => {
  if (isConnectivityError(error)) {
    return "connectivity"
  }

  const status = toHttpError(error).status
  if (INVALID_CREDENTIAL_STATUSES.has(status)) {
    return "invalid_credentials"
  }

  return "server_error"
}

export const classifyPostLoginUserFailure = (
  error: unknown
): Exclude<SignInFailure, "invalid_credentials"> => {
  const failure = classifySignInFailure(error)

  return failure === "invalid_credentials" ? "server_error" : failure
}

export const doLogin = async ({
  data = {},
  params = {},
}): Promise<LoginResponse> => {
  try {
    const response = await httpClient.post<LoginResponse>(
      "/api/v1/sessions",
      data,
      params
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const doLogout = async (params = {}): Promise<void> => {
  try {
    await httpClient.delete("/api/v1/sessions", params)

    return undefined
  } catch {
    return undefined
  }
}

const getSessionTimeout = async (): Promise<number> => {
  try {
    const response = await httpClient.get<number>("/check_session_timeout")

    if (response.status === 200) {
      return response.data
    }

    return 0
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const fetchSessionValidity = async (): Promise<boolean> => {
  const sessionTimeout = await getSessionTimeout()

  if (sessionTimeout > 0) {
    return true
  }

  return false
}

type UserAttributes = {
  [K in keyof User]: User[K]
}

interface GetUserResponse {
  data: {
    attributes: UserAttributes
    id: string
    type: "users"
  }
}

// We are not returning a User class instance but instead a pojo that represents a user
export const getUser = async (params = {}): Promise<User> => {
  try {
    const response = await httpClient.get<GetUserResponse>("/api/v1/user", {
      params,
    })

    return {
      ...response.data.data.attributes,
      id: response.data.data.id,
      type: response.data.data.type,
    } as unknown as User
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}
